import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, of, shareReplay, tap } from 'rxjs';
import { HttpService } from './http.service';
import { UserService } from './user.service';
import {
  ActiveSessionResponse,
  AdminLoginResponse,
  AdminRecoveryCodesResponse,
  AdminVerifyOtpRequest,
  AuthResponse,
  ChangePasswordRequest,
  LoginRequest,
  TokenResponse,
} from '../constants/models/auth.model';
import { UserResponse } from '../constants/models/user.model';

const ACCESS_TOKEN_KEY = 'card_access_token';
const REFRESH_TOKEN_KEY = 'card_refresh_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);

  private readonly _currentUser = signal<UserResponse | null>(null);
  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => !!this._currentUser());

  private readonly _currentUserRoles = signal<string[]>([]);
  readonly currentUserRoles = this._currentUserRoles.asReadonly();
  readonly isSuperAdmin = computed(() => this._currentUserRoles().includes('SUPER_ADMIN'));

  // Miroir exact du garde-fou backend (AdminAuthServiceImpl) : un compte n'est traité comme
  // MAIL_OPERATOR restreint que si c'est là son SEUL rôle non-USER — jamais si un rôle plus
  // large (ADMIN/SUPER_ADMIN) est présent en plus.
  readonly isMailOperatorOnly = computed(() => {
    const nonUserRoles = this._currentUserRoles().filter(r => r !== 'USER');
    return nonUserRoles.length === 1 && nonUserRoles[0] === 'MAIL_OPERATOR';
  });

  private readonly _rolesLoaded = signal(false);
  private rolesRequest$: Observable<string[]> | null = null;

  get accessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  get refreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.accessToken;
  }

  adminLogin(request: LoginRequest): Observable<AdminLoginResponse> {
    return this.http.post<AdminLoginResponse>('/admin/auth/login', request);
  }

  verifyAdminOtp(request: AdminVerifyOtpRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/admin/auth/verify-otp', request).pipe(
      tap(res => {
        localStorage.setItem(ACCESS_TOKEN_KEY, res.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, res.refreshToken);
      })
    );
  }

  refresh(): Observable<TokenResponse> {
    return this.http.post<TokenResponse>('/auth/refresh', { refreshToken: this.refreshToken }).pipe(
      tap(res => {
        localStorage.setItem(ACCESS_TOKEN_KEY, res.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, res.refreshToken);
      })
    );
  }

  logout(): void {
    this.http.post('/auth/logout', { refreshToken: this.refreshToken }).subscribe({ error: () => {} });
    this.clearSession();
    this.router.navigate(['/auth/login']);
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.http.post<void>('/auth/change-password', request).pipe(
      tap(() => {
        this.clearSession();
        this.router.navigate(['/auth/login']);
      })
    );
  }

  getSessions(): Observable<ActiveSessionResponse> {
    return this.http.get<ActiveSessionResponse>('/auth/sessions');
  }

  revokeSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`/auth/sessions/${sessionId}`);
  }

  setCurrentUser(user: UserResponse): void {
    this._currentUser.set(user);
  }

  setCurrentUserRoles(roles: string[]): void {
    this._currentUserRoles.set(roles);
    this._rolesLoaded.set(true);
  }

  /**
   * Charge les rôles du compte courant via /users/me/roles (self-service, accessible même à
   * un compte restreint comme MAIL_OPERATOR — contrairement à getWithRoles(id) qui exige
   * USER_READ). Mis en cache : n'appelle le backend qu'une fois par session.
   */
  ensureRolesLoaded(): Observable<string[]> {
    if (this._rolesLoaded()) return of(this._currentUserRoles());

    if (!this.rolesRequest$) {
      this.rolesRequest$ = this.userService.getMyRoles().pipe(
        tap(res => this.setCurrentUserRoles(res.roles)),
        map(res => res.roles),
        shareReplay(1)
      );
    }
    return this.rolesRequest$;
  }

  generateRecoveryCodes(): Observable<AdminRecoveryCodesResponse> {
    return this.http.post<AdminRecoveryCodesResponse>('/admin/auth/recovery-codes');
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>('/auth/forgot-password', { email });
  }

  resetPassword(request: { token: string; newPassword: string }): Observable<void> {
    return this.http.post<void>('/auth/reset-password', request);
  }

  logoutAll(): Observable<void> {
    return this.http.post<void>('/auth/logout-all', {});
  }

  logoutOthers(): Observable<void> {
    return this.http.post<void>('/auth/logout-others', { refreshToken: this.refreshToken });
  }

  private clearSession(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    this._currentUser.set(null);
    this._currentUserRoles.set([]);
    this._rolesLoaded.set(false);
    this.rolesRequest$ = null;
  }
}
